import { useCallback, useSyncExternalStore } from 'react'
import { API_ROUTES, type Slip } from '@brochure/shared'
import { apiFetch } from '@/lib/api'
import { EMPTY_SLIP, draftAfterPlaced, toPlaceSlip, type DraftSlip } from '@/lib/slip'
import { EMPTY_SLIP_STATE, createSlipStore } from '@/lib/slip-store'

// Letto solo a chiamata (non all'import): l'ambiente Vitest 'node' non ha `localStorage`.
const storage = () => (typeof localStorage === 'undefined' ? null : localStorage)
const store = createSlipStore(storage)
const noop = () => () => {}

/**
 * Schedina in bozza del torneo: una sola per scheda (`slip-store`), condivisa dalle pagine ZambeBet montate e salvata
 * in `localStorage`, così sopravvive ai cambi di pagina e alle ricariche. `isPlacing` è vero mentre una POST è in
 * volo da qualunque pagina: "Gioca" resta spento ovunque. L'attesa delle giocate live non sta qui: è lo stato
 * `pending` della schedina sul server (vedi `LiveSlips`).
 */
export function useSlip(tournamentId: string | undefined, guestToken?: string) {
  const subscribe = useCallback((listener: () => void) => (tournamentId ? store.subscribe(tournamentId, listener) : noop()), [tournamentId])
  const getSnapshot = useCallback(() => (tournamentId ? store.get(tournamentId) : EMPTY_SLIP_STATE), [tournamentId])
  const { slip, isPlacing } = useSyncExternalStore(subscribe, getSnapshot)

  // Gli aggiornamenti funzionali leggono lo store, non il render: due tocchi nello stesso tick non si sovrascrivono.
  const setSlip = useCallback(
    (next: DraftSlip | ((prev: DraftSlip) => DraftSlip)) => {
      if (!tournamentId) return
      store.setSlip(tournamentId, typeof next === 'function' ? next(store.get(tournamentId).slip) : next)
    },
    [tournamentId],
  )
  const clear = useCallback(() => setSlip(EMPTY_SLIP), [setSlip])

  /**
   * Invia la bozza e restituisce la schedina: `open` (201) o, con una partita in corso, `pending` (202) da confermare
   * dal server. In entrambi i casi la schedina esiste già: la bozza si svuota, ma solo se non è cambiata durante l'invio.
   * Gli errori arrivano a chi chiama.
   */
  const place = useCallback(
    async (draft: DraftSlip): Promise<Slip> => {
      if (!tournamentId) throw new Error('missing tournament')
      // Una POST alla volta per torneo, anche se un'altra istanza montata la chiama.
      if (store.get(tournamentId).isPlacing) throw new Error('already placing')
      store.setPlacing(tournamentId, true)
      try {
        const placed = await apiFetch<Slip>(`${API_ROUTES.tournaments}/${tournamentId}/slips`, { method: 'POST', guestToken, json: toPlaceSlip(draft) })
        store.setSlip(tournamentId, draftAfterPlaced(draft, store.get(tournamentId).slip))
        return placed
      } finally {
        store.setPlacing(tournamentId, false)
      }
    },
    [tournamentId, guestToken],
  )

  return { slip, setSlip, clear, place, isPlacing }
}
